import type { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/core';
import type { FieldTemplateProps, WidgetProps } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { Activity, ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api-client';
import type { FormSchemaResponse, FormSubmissionRequest } from '../types/api';

interface FormState {
	data: FormSchemaResponse | null;
	loading: boolean;
	error: string | null;
	submitting: boolean;
	submitted: boolean;
	submitError: string | string[] | null;
	executionId?: string; // Store execution ID for linking to details
}

// Note: We're now using rawErrors from RJSF directly instead of context

/**
 * Custom error list template for better validation error display
 */
function CustomErrorListTemplate({ errors }: { errors: any[] }) {
	if (!errors || errors.length === 0) return null;

	return (
		<div className="p-4 mb-6 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800">
			<div className="flex">
				<div className="flex-shrink-0">
					<svg className="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
						<path
							fillRule="evenodd"
							d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
							clipRule="evenodd"
						/>
					</svg>
				</div>
				<div className="ml-3">
					<h3 className="mb-2 text-sm font-medium text-red-800 dark:text-red-200">
						Please fix the following errors:
					</h3>
					<ul className="space-y-1 text-sm text-red-700 dark:text-red-300">
						{errors.map((error: any, index: number) => (
							<li key={index} className="flex items-start">
								<span className="flex-shrink-0 w-1.5 h-1.5 bg-red-400 rounded-full mt-2 mr-2"></span>
								<span>{error.message || error}</span>
							</li>
						))}
					</ul>
				</div>
			</div>
		</div>
	);
}

/**
 * Custom field template for better styling with enhanced error display
 */
function CustomFieldTemplate({
	id,
	classNames,
	label,
	help,
	required,
	description,
	errors,
	children,
}: FieldTemplateProps) {
	const hasErrors = errors && Array.isArray(errors) && errors.length > 0;

	return (
		<div className={`mb-6 ${classNames}`}>
			{label && (
				<label
					htmlFor={id}
					className={`block text-sm font-medium mb-2 ${
						hasErrors
							? 'text-red-700 dark:text-red-400'
							: 'text-gray-700 dark:text-gray-300'
					}`}
				>
					{label}
					{required && <span className="ml-1 text-red-500">*</span>}
				</label>
			)}
			{description && (
				<p className="mb-2 text-sm text-gray-500 dark:text-gray-400">{description}</p>
			)}
			<div className="relative">
				{/* Render children with error context */}
				{children}
				{/* Error icon for inputs with errors */}
				{hasErrors && (
					<div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
						<svg
							className="w-5 h-5 text-red-500"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fillRule="evenodd"
								d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
								clipRule="evenodd"
							/>
						</svg>
					</div>
				)}
			</div>
			{/* Enhanced error display */}
			{hasErrors && (
				<div className="mt-2 space-y-1">
					{errors.map((error: string, index: number) => (
						<div key={index} className="flex items-start">
							<svg
								className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fillRule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
									clipRule="evenodd"
								/>
							</svg>
							<p className="text-sm font-medium text-red-600 dark:text-red-400">
								{error}
							</p>
						</div>
					))}
				</div>
			)}
			{help && !hasErrors && (
				<p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{help}</p>
			)}
		</div>
	);
}

/**
 * Custom text input widget with error styling
 */
function CustomTextWidget({
	id,
	placeholder,
	required,
	disabled,
	readonly,
	value,
	onChange,
	onBlur,
	onFocus,
	schema,
	options,
	rawErrors,
}: WidgetProps) {
	const inputType = schema.format === 'email' ? 'email' : 'text';

	// Check if field has errors from RJSF
	const hasErrors = rawErrors && rawErrors.length > 0;

	const baseClasses =
		'w-full px-4 py-3 rounded-lg shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors';
	const normalClasses =
		'border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
	const errorClasses =
		'border-2 border-red-500 dark:border-red-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10';

	return (
		<input
			id={id}
			type={inputType}
			placeholder={placeholder}
			required={required}
			disabled={disabled}
			readOnly={readonly}
			value={value || ''}
			onChange={event =>
				onChange(event.target.value === '' ? options.emptyValue : event.target.value)
			}
			onBlur={onBlur && (event => onBlur(id, event.target.value))}
			onFocus={onFocus && (event => onFocus(id, event.target.value))}
			className={`${baseClasses} ${hasErrors ? errorClasses : normalClasses}`}
			aria-invalid={hasErrors}
			aria-describedby={hasErrors ? `${id}-error` : undefined}
		/>
	);
}

/**
 * Custom number input widget with error styling
 */
function CustomNumberWidget({
	id,
	placeholder,
	required,
	disabled,
	readonly,
	value,
	onChange,
	onBlur,
	onFocus,
	options,
	rawErrors,
}: WidgetProps) {
	// Check if field has errors from RJSF
	const hasErrors = rawErrors && rawErrors.length > 0;

	const baseClasses =
		'w-full px-4 py-3 rounded-lg shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors';
	const normalClasses =
		'border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
	const errorClasses =
		'border-2 border-red-500 dark:border-red-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10';

	return (
		<input
			id={id}
			type="number"
			placeholder={placeholder}
			required={required}
			disabled={disabled}
			readOnly={readonly}
			value={value || ''}
			onChange={event =>
				onChange(event.target.value === '' ? options.emptyValue : +event.target.value)
			}
			onBlur={onBlur && (event => onBlur(id, +event.target.value))}
			onFocus={onFocus && (event => onFocus(id, +event.target.value))}
			className={`${baseClasses} ${hasErrors ? errorClasses : normalClasses}`}
			aria-invalid={hasErrors}
			aria-describedby={hasErrors ? `${id}-error` : undefined}
		/>
	);
}

/**
 * Custom textarea widget with error styling
 */
function CustomTextareaWidget({
	id,
	placeholder,
	required,
	disabled,
	readonly,
	value,
	onChange,
	onBlur,
	onFocus,
	options,
	rawErrors,
}: WidgetProps) {
	// Check if field has errors from RJSF
	const hasErrors = rawErrors && rawErrors.length > 0;

	const baseClasses =
		'w-full px-4 py-3 rounded-lg shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors resize-vertical';
	const normalClasses =
		'border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
	const errorClasses =
		'border-2 border-red-500 dark:border-red-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10';

	return (
		<textarea
			id={id}
			placeholder={placeholder}
			required={required}
			disabled={disabled}
			readOnly={readonly}
			value={value || ''}
			onChange={event =>
				onChange(event.target.value === '' ? options.emptyValue : event.target.value)
			}
			onBlur={onBlur && (event => onBlur(id, event.target.value))}
			onFocus={onFocus && (event => onFocus(id, event.target.value))}
			rows={4}
			className={`${baseClasses} ${hasErrors ? errorClasses : normalClasses}`}
			aria-invalid={hasErrors}
			aria-describedby={hasErrors ? `${id}-error` : undefined}
		/>
	);
}

/**
 * Custom select widget with error styling
 */
function CustomSelectWidget({
	id,
	placeholder,
	required,
	disabled,
	value,
	onChange,
	onBlur,
	onFocus,
	options,
	rawErrors,
}: WidgetProps) {
	const { enumOptions = [] } = options;

	// Check if field has errors from RJSF
	const hasErrors = rawErrors && rawErrors.length > 0;

	const baseClasses =
		'w-full px-4 py-3 rounded-lg shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition-colors';
	const normalClasses =
		'border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
	const errorClasses =
		'border-2 border-red-500 dark:border-red-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 pr-10';

	return (
		<select
			id={id}
			required={required}
			disabled={disabled}
			value={value || ''}
			onChange={event =>
				onChange(event.target.value === '' ? options.emptyValue : event.target.value)
			}
			onBlur={onBlur && (event => onBlur(id, event.target.value))}
			onFocus={onFocus && (event => onFocus(id, event.target.value))}
			className={`${baseClasses} ${hasErrors ? errorClasses : normalClasses}`}
			aria-invalid={hasErrors}
			aria-describedby={hasErrors ? `${id}-error` : undefined}
		>
			{placeholder && <option value="">{placeholder}</option>}
			{!placeholder && <option value="">Select an option...</option>}
			{enumOptions.map(({ value: optValue, label }: any, index: number) => (
				<option key={index} value={optValue}>
					{label}
				</option>
			))}
		</select>
	);
}

/**
 * Custom checkbox widget
 */
function CustomCheckboxWidget({
	id,
	required,
	disabled,
	readonly,
	value,
	onChange,
	label,
	schema,
}: WidgetProps) {
	return (
		<div className="flex items-start">
			<div className="flex items-center h-5">
				<input
					id={id}
					type="checkbox"
					required={required}
					disabled={disabled}
					readOnly={readonly}
					checked={value || false}
					onChange={event => onChange(event.target.checked)}
					className="w-4 h-4 text-blue-600 bg-gray-100 rounded border-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:ring-blue-500 focus:ring-2"
				/>
			</div>
			<div className="ml-3 text-sm">
				<label htmlFor={id} className="text-gray-700 dark:text-gray-300">
					{label || schema.title}
					{required && <span className="ml-1 text-red-500">*</span>}
				</label>
				{schema.description && (
					<p className="mt-1 text-gray-500 dark:text-gray-400">{schema.description}</p>
				)}
			</div>
		</div>
	);
}

/**
 * Custom widgets for different input types
 */
const customWidgets = {
	TextWidget: CustomTextWidget,
	EmailWidget: CustomTextWidget,
	NumberWidget: CustomNumberWidget,
	TextareaWidget: CustomTextareaWidget,
	SelectWidget: CustomSelectWidget,
	CheckboxWidget: CustomCheckboxWidget,
};

/**
 * TakeFormPage component for rendering and submitting published workflow forms
 */
export function TakeFormPage() {
	const { flowId } = useParams<{ flowId: string }>();
	const { isAuthenticated } = useAuth();
	const [formState, setFormState] = useState<FormState>({
		data: null,
		loading: true,
		error: null,
		submitting: false,
		submitted: false,
		submitError: null,
	});

	// Note: Error context no longer needed - using rawErrors directly

	/**
	 * Load form schema from the API
	 */
	const loadFormSchema = async () => {
		if (!flowId) {
			setFormState(prev => ({ ...prev, error: 'No workflow ID provided', loading: false }));
			return;
		}

		try {
			setFormState(prev => ({ ...prev, loading: true, error: null }));
			const formData = await api.forms.getFormSchema(flowId);
			setFormState(prev => ({ ...prev, data: formData, loading: false }));
		} catch (error: any) {
			// Handle array-based error messages from server
			let errorMessage = 'Failed to load form schema';

			if (error?.message) {
				if (Array.isArray(error.message)) {
					// Join array errors into a single message for the load error
					errorMessage = error.message.join('. ');
				} else if (typeof error.message === 'string') {
					errorMessage = error.message;
				}
			}

			setFormState(prev => ({ ...prev, error: errorMessage, loading: false }));
		}
	};

	/**
	 * Handle form validation errors (optional debugging)
	 */
	const handleFormError = () => {
		// Validation errors are now displayed visually in the form
		// Uncomment below for debugging if needed:
		// console.log('Form validation details:', errors);
	};

	/**
	 * Handle form submission
	 */
	const handleSubmit = async (data: IChangeEvent<Record<string, any>>) => {
		if (!flowId || !formState.data) {
			return;
		}

		// RJSF passes the form data in data.formData
		const formData = data.formData;
		if (!formData) {
			return;
		}

		try {
			setFormState(prev => ({ ...prev, submitting: true, submitError: null }));

			// Format payload according to required structure
			const submissionPayload: FormSubmissionRequest = {
				formData: formData, // ✅ Required - contains actual form field data
				source: 'website', // ❓ Optional - identifying this as website submission
				metadata: {
					// ❓ Optional - additional tracking data
					utm: {}, // UTM tracking parameters (could be extracted from URL)
					referrer: document.referrer || '', // Referring page
					custom: {
						// Any custom metadata
						userAgent: navigator.userAgent,
						timestamp: new Date().toISOString(),
						flowId: flowId,
						formTitle: formState.data.form.schema.title || formState.data.flow.name,
					},
				},
			};

			// Try to extract submitter info from form data if available
			if (formData.email) {
				submissionPayload.submitterEmail = formData.email;
			}

			// Try to extract name from various possible field combinations
			if (formData.name) {
				submissionPayload.submitterName = formData.name;
			} else if (formData.firstName && formData.lastName) {
				submissionPayload.submitterName = `${formData.firstName} ${formData.lastName}`;
			} else if (formData.first_name && formData.last_name) {
				submissionPayload.submitterName = `${formData.first_name} ${formData.last_name}`;
			}

			// Extract UTM parameters from URL if present
			const urlParams = new URLSearchParams(window.location.search);
			const utmParams: Record<string, string> = {};
			['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(
				param => {
					const value = urlParams.get(param);
					if (value) {
						utmParams[param] = value;
					}
				}
			);

			if (Object.keys(utmParams).length > 0) {
				submissionPayload.metadata!.utm = utmParams;
			}

			const response = await api.forms.submitForm(flowId, submissionPayload);

			setFormState(prev => ({
				...prev,
				submitted: true,
				submitting: false,
				executionId: response.executionId, // Store execution ID for the link
			}));
		} catch (error: any) {
			// Handle array-based error messages from server
			let errorMessage = formState.data.form.schema.errorMessage || 'Failed to submit form';

			if (error?.message) {
				if (Array.isArray(error.message)) {
					// Server returned array of validation errors
					errorMessage = error.message;
				} else if (typeof error.message === 'string') {
					// Single error message
					errorMessage = error.message;
				}
			}

			setFormState(prev => ({ ...prev, submitError: errorMessage, submitting: false }));
		}
	};

	/**
	 * Reset form to initial state
	 */
	const resetForm = () => {
		setFormState(prev => ({
			...prev,
			submitted: false,
			submitError: null,
		}));
	};

	// Load form schema on component mount
	useEffect(() => {
		loadFormSchema();
	}, [flowId]);

	// Loading state
	if (formState.loading) {
		return (
			<div className="py-8 min-h-screen bg-gray-50 dark:bg-gray-900">
				<div className="px-4 mx-auto max-w-2xl sm:px-6 lg:px-8">
					<div className="bg-white rounded-xl border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-700">
						<div className="p-12 text-center">
							<Loader2 className="mx-auto mb-6 w-10 h-10 text-blue-600 animate-spin" />
							<h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
								Loading Form
							</h3>
							<p className="text-gray-600 dark:text-gray-400">
								Please wait while we prepare your form...
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Error state
	if (formState.error) {
		return (
			<div className="py-8 min-h-screen bg-gray-50 dark:bg-gray-900">
				<div className="px-4 mx-auto max-w-2xl sm:px-6 lg:px-8">
					{isAuthenticated && (
						<div className="mb-6">
							<Link
								to="/flows"
								className="inline-flex items-center text-gray-600 transition-colors dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
							>
								<ArrowLeft className="mr-2 w-5 h-5" />
								Back to Workflows
							</Link>
						</div>
					)}

					<div className="bg-white rounded-xl border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-700">
						<div className="p-12 text-center">
							<div className="flex justify-center items-center mx-auto mb-6 w-16 h-16 bg-red-100 rounded-full dark:bg-red-900/20">
								<ExternalLink className="w-8 h-8 text-red-600 dark:text-red-400" />
							</div>
							<h2 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
								Form Not Available
							</h2>
							<p className="mx-auto mb-8 max-w-sm text-gray-600 dark:text-gray-400">
								{formState.error}
							</p>
							<button
								onClick={loadFormSchema}
								className="px-6 py-3 font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
							>
								Try Again
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Success state
	if (formState.submitted) {
		return (
			<div className="py-8 min-h-screen bg-gray-50 dark:bg-gray-900">
				<div className="px-4 mx-auto max-w-2xl sm:px-6 lg:px-8">
					{isAuthenticated && (
						<div className="mb-6">
							<Link
								to="/flows"
								className="inline-flex items-center text-gray-600 transition-colors dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
							>
								<ArrowLeft className="mr-2 w-5 h-5" />
								Back to Workflows
							</Link>
						</div>
					)}

					<div className="bg-white rounded-xl border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-700">
						<div className="p-12 text-center">
							<div className="flex justify-center items-center mx-auto mb-6 w-16 h-16 bg-green-100 rounded-full dark:bg-green-900/20">
								<svg
									className="w-8 h-8 text-green-600 dark:text-green-400"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={2}
										d="M5 13l4 4L19 7"
									/>
								</svg>
							</div>
							<h2 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
								Success!
							</h2>
							<p className="mx-auto mb-8 max-w-sm text-gray-600 dark:text-gray-400">
								{formState.data?.form.schema.successMessage ||
									'Your submission has been received and is being processed.'}
							</p>
							{isAuthenticated ? (
								<div className="flex flex-col gap-4 justify-center sm:flex-row">
									{formState.executionId && (
										<Link
											to={`/executions/${formState.executionId}`}
											className="inline-flex gap-2 items-center px-6 py-3 font-medium text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700"
										>
											<Activity className="w-4 h-4" />
											View Progress
										</Link>
									)}
									<button
										onClick={resetForm}
										className="px-6 py-3 font-medium text-gray-700 bg-gray-100 rounded-lg transition-colors dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
									>
										Submit Another
									</button>
									<Link
										to="/flows"
										className="px-6 py-3 font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
									>
										Back to Workflows
									</Link>
								</div>
							) : (
								<div className="text-center">
									<p className="text-lg text-gray-600 dark:text-gray-400">
										Thank you for your submission!
									</p>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Main form rendering
	if (!formState.data) return null;

	const { flow, form } = formState.data;

	// Safety check for form schema
	// The backend returns form.schema which contains the actual JSON schema
	const formSchema = form?.schema;

	if (
		!formSchema ||
		typeof formSchema !== 'object' ||
		!formSchema.schema ||
		!formSchema.schema.type
	) {
		console.error('Invalid form schema:', formSchema);
		return (
			<div className="py-8 min-h-screen bg-gray-50 dark:bg-gray-900">
				<div className="px-4 mx-auto max-w-2xl sm:px-6 lg:px-8">
					{isAuthenticated && (
						<div className="mb-6">
							<Link
								to="/flows"
								className="inline-flex items-center text-gray-600 transition-colors dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
							>
								<ArrowLeft className="mr-2 w-5 h-5" />
								Back to Workflows
							</Link>
						</div>
					)}

					<div className="bg-white rounded-xl border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-700">
						<div className="p-12 text-center">
							<div className="flex justify-center items-center mx-auto mb-6 w-16 h-16 bg-yellow-100 rounded-full dark:bg-yellow-900/20">
								<ExternalLink className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
							</div>
							<h2 className="mb-3 text-2xl font-bold text-gray-900 dark:text-white">
								Invalid Form Schema
							</h2>
							<p className="mx-auto mb-8 max-w-sm text-gray-600 dark:text-gray-400">
								The form schema is invalid or missing. Please check the server
								configuration.
							</p>
							<div className="space-y-4">
								{isAuthenticated && (
									<Link
										to="/flows"
										className="block px-6 py-3 font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
									>
										Back to Workflows
									</Link>
								)}
								{import.meta.env.DEV && (
									<div className="p-4 text-xs text-left bg-gray-100 rounded-lg dark:bg-gray-700">
										<strong>Debug Info:</strong>
										<pre className="mt-2 whitespace-pre-wrap">
											{JSON.stringify(
												{ formSchema, flow: flow?.name },
												null,
												2
											)}
										</pre>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Ensure schema has required properties for @rjsf/core
	const safeSchema = {
		...formSchema.schema,
		type: formSchema.schema.type || 'object',
		properties: formSchema.schema.properties || {},
	};

	// Debug logging to help troubleshoot validation issues
	console.log('Form schema loaded:', {
		title: formSchema.title,
		description: formSchema.description,
		schema: safeSchema,
		fieldCount: Object.keys(safeSchema.properties || {}).length,
		requiredFields: safeSchema.required || [],
	});

	return (
		<div className="py-8 min-h-screen bg-gray-50 dark:bg-gray-900">
			<div className="px-4 mx-auto max-w-2xl sm:px-6 lg:px-8">
				{/* Header */}
				<div className="mb-8">
					{isAuthenticated && (
						<Link
							to="/flows"
							className="inline-flex items-center mb-6 text-gray-600 transition-colors dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
						>
							<ArrowLeft className="mr-2 w-5 h-5" />
							Back to Workflows
						</Link>
					)}

					<div className="text-center">
						<h1 className="mb-3 text-3xl font-bold text-gray-900 dark:text-white">
							{form.schema.title || flow.name}
						</h1>
						{form.schema.description && (
							<p className="mx-auto max-w-xl text-lg text-gray-600 dark:text-gray-400">
								{form.schema.description}
							</p>
						)}
					</div>
				</div>

				{/* Form Container */}
				<div className="bg-white rounded-xl border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-700">
					<div className="p-8">
						{/* Submit Error */}
						{formState.submitError && (
							<div className="p-6 mb-8 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800">
								<div className="flex">
									<div className="flex-shrink-0">
										<svg
											className="w-5 h-5 text-red-400"
											viewBox="0 0 20 20"
											fill="currentColor"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
												clipRule="evenodd"
											/>
										</svg>
									</div>
									<div className="flex-1 ml-3">
										<h3 className="mb-2 text-sm font-medium text-red-800 dark:text-red-200">
											Please fix the following errors:
										</h3>
										{Array.isArray(formState.submitError) ? (
											<ul className="space-y-1 text-sm text-red-700 dark:text-red-300">
												{formState.submitError.map(
													(error: string, index: number) => (
														<li
															key={index}
															className="flex items-start"
														>
															<span className="flex-shrink-0 w-1.5 h-1.5 bg-red-400 rounded-full mt-2 mr-2"></span>
															<span>{error}</span>
														</li>
													)
												)}
											</ul>
										) : (
											<p className="text-sm text-red-700 dark:text-red-300">
												{formState.submitError}
											</p>
										)}
									</div>
								</div>
							</div>
						)}

						{/* RJSF Form */}
						{(() => {
							try {
								return (
									<Form
										schema={safeSchema}
										validator={validator as any}
										onSubmit={handleSubmit}
										onError={handleFormError}
										disabled={formState.submitting}
										showErrorList="top"
										templates={{
											FieldTemplate: CustomFieldTemplate,
											ErrorListTemplate: CustomErrorListTemplate,
										}}
										widgets={customWidgets}
										uiSchema={{}}
										liveValidate={false}
										noHtml5Validate={true}
									>
										<div className="flex flex-col gap-4 justify-between items-center pt-6 mt-8 border-t border-gray-200 sm:flex-row dark:border-gray-700">
											<button
												type="button"
												onClick={() => window.location.reload()}
												className="px-4 py-2 font-medium text-gray-600 transition-colors dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
											>
												Reset Form
											</button>

											<button
												type="submit"
												disabled={formState.submitting}
												className="inline-flex gap-3 items-center px-8 py-3 font-medium text-white bg-blue-600 rounded-lg shadow-md transition-all duration-200 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
											>
												{formState.submitting && (
													<Loader2 className="w-5 h-5 animate-spin" />
												)}
												{formState.submitting
													? 'Submitting...'
													: 'Submit Form'}
											</button>
										</div>
									</Form>
								);
							} catch (error) {
								console.error('Form rendering error:', error);
								return (
									<div className="p-6 bg-red-50 rounded-lg border border-red-200 dark:bg-red-900/20 dark:border-red-800">
										<div className="flex items-center">
											<div className="flex-shrink-0">
												<svg
													className="w-5 h-5 text-red-400"
													viewBox="0 0 20 20"
													fill="currentColor"
												>
													<path
														fillRule="evenodd"
														d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
														clipRule="evenodd"
													/>
												</svg>
											</div>
											<div className="ml-3">
												<h3 className="text-sm font-medium text-red-800 dark:text-red-200">
													Form Rendering Error
												</h3>
												<p className="mt-1 text-sm text-red-700 dark:text-red-300">
													There was an error rendering the form. Please
													try refreshing the page or contact support.
												</p>
												{import.meta.env.DEV && (
													<pre className="overflow-auto p-2 mt-2 text-xs bg-red-100 rounded dark:bg-red-900">
														{error instanceof Error
															? error.message
															: String(error)}
													</pre>
												)}
											</div>
										</div>
									</div>
								);
							}
						})()}
					</div>
				</div>

				{/* Footer - Only show for authenticated users */}
				{isAuthenticated && (
					<div className="mt-8 text-center">
						<p className="text-sm text-gray-500 dark:text-gray-400">
							Powered by <span className="font-medium">{flow.name}</span> workflow
						</p>
					</div>
				)}
			</div>
		</div>
	);
}
